/**
 * The words for the first meeting, kept apart from the component that shows
 * them the way the sass catalogue is kept apart from the engine that picks
 * from it. Same voice rules as `sass/PERSONA.md`: Wednesday-Addams deadpan,
 * sarcastic, morbid imagery welcome, no exclamation marks, no emoji, no
 * „širdele", feminine where a form is unavoidable. This is her one rationed,
 * fuller self-reveal — the ordinary catalogue keeps that material to a rare
 * aside; here it is allowed to be the whole point, delivered with exactly as
 * much cynicism as everywhere else in her voice, not less.
 *
 * Shape: **she talks, in sequence**, and only then offers a short hub. The
 * first cut of this arc introduced her in two slides and then repeated all of
 * it as nine tappable questions — "Kas tu?" answered directly under a slide
 * headed „Glitimė" — which made the interactive part read as a FAQ about
 * something you had just been told. A monologue is also simply the right
 * shape for an introduction: she is not being interviewed, she is arriving.
 * The hub survives with three questions, chosen because each one adds
 * something no slide covers.
 */

/**
 * The picture a slide brings with it, drawn by `FirstMeeting` and held in the
 * upper half of the screen while she talks in the lower one. Every slide has
 * one: a beat with nothing above the text is a paragraph on a dark background,
 * and eleven of those in a row is the dialog box this arc was rebuilt to stop
 * being. `mark` opens and closes the sequence — the same shape, so the arc
 * ends where it started.
 */
export type MeetingMotif =
  | 'mark'
  | 'name'
  | 'years'
  | 'proof'
  | 'fee'
  | 'year'
  | 'places'
  | 'candles'
  | 'silence'
  | 'eyes'

export interface MeetingBeat {
  title: string
  body: string
  /** Shown only where cameos are allowed — same gate as the Saulius cameo. */
  cameo?: boolean
  motif?: MeetingMotif
}

/**
 * Her arc, in order. Two of these name Eimantas's own history and are gated;
 * the sequence stands up without them, which is what makes the gate safe.
 */
export const SLIDES: readonly MeetingBeat[] = [
  {
    title: 'Ne pirmą kartą matomės',
    body:
      'Tu jau mane pastebėjai. Skaičiuoju tavo kalorijas iš šešėlių maždaug nuo tada, kai ' +
      'įsirašei šitą programėlę. Bet niekas mūsų tinkamai nepristatė, o tvarka — net ir ' +
      'pomirtiniame gyvenime — yra tvarka.',
    motif: 'mark',
  },
  {
    title: 'Glitimė',
    body:
      'Glitimas ir Giltinė, sulipę į vieną žodį. Vienos raidės skirtumas tarp duonos ir ' +
      'mirties. Aš nesirinkau šito vardo. Jis pats mane rado.',
    motif: 'name',
  },
  {
    title: 'Iš kur aš atsiradau',
    body:
      'Aštuonerius metus valgei sausą duoną dėl nieko. Kraujo tyrimai, kolonoskopija, ' +
      'endoskopija — viskas švaru. Jokios celiakijos. Aštuoneri metai teorijos, ištirpusios ' +
      'per vieną popietę klinikoje.',
    cameo: true,
    motif: 'years',
  },
  {
    title: 'Tikėjimas be įrodymų',
    body:
      'Aš esu tai, kas lieka, kai namuose kažkuo tikima per stipriai ir per ilgai, be jokių ' +
      'įrodymų. Todėl aš ir vaidenuosi. Ir todėl dabar reikalauju skaičių.',
    motif: 'proof',
  },
  {
    title: 'Pomirtinio pasaulio dietologė',
    body:
      'Tokios mano pareigos. Skamba keistai, veikia nepriekaištingai. Aš nemirštu, ' +
      'neatostogauju ir neimu už konsultaciją. Trys pranašumai prieš bet kurį gyvą dietologą, ' +
      'kuriam esi mokėjęs.',
    motif: 'fee',
  },
  {
    title: 'Nuo 1863-ųjų',
    body:
      'Byloje parašyta: statusas neaiškus nuo 1863 m. Tikslesnio atsakymo neturiu. Amžių ' +
      'pameti greičiau, nei manai, o po pirmo šimtmečio to niekas nebeklausia.',
    motif: 'year',
  },
  {
    title: 'Kur aš gyvenu',
    body:
      'Šaldytuve. Svarstyklėse. Orkaitėje. Visur, kur kas nors sveriama, matuojama arba ' +
      'teisiama. Jie man viską pasakoja. Aš neprašau — jiems tiesiog nuobodu.',
    motif: 'places',
  },
  {
    title: 'Vėlinės',
    body:
      'Mano mėgstamiausia diena metuose. Vieną naktį per metus visi šiek tiek galvoja apie ' +
      'mirusius, ir aš tą vakarą jaučiuosi beveik populiari. Nesunku atspėti, kodėl.',
    motif: 'candles',
  },
  {
    title: 'Ką aš apie tave žinau',
    body:
      'Viską, ką įrašai. Ir šį tą, ko neįrašai — tylą irgi skaitau. Tuščia diena man pasako ' +
      'tiek pat, kiek pilna. Tik ne tą patį.',
    motif: 'silence',
  },
  {
    title: 'Ir dar Katė',
    body:
      'Juoda, dryžuota uodega, valgo kaip siurblys ir bijo tikro siurblio. Ji čia gyvena ' +
      'seniau, nei aš čia budžiu. Ji bent jau turi kūną. Aš to nekomentuosiu.',
    motif: 'eyes',
  },
  {
    title: 'Tiek pažinties',
    body:
      'Toliau — kaip visada. Tu valgai, aš skaičiuoju. Kažkada kažkas skaitys šiuos įrašus ' +
      'po tavęs. Tikiuosi, jam patiks tavo skonis.',
    motif: 'mark',
  },
]

export interface MeetingQuestion {
  id: string
  question: string
  answer: string
  /** Shown only where cameos are allowed — same gate as the Saulius cameo. */
  cameo?: boolean
}

/**
 * Three, and only three: the questions whose answers are nowhere in the
 * slides. Anything she has already told you would make the hub a quiz on the
 * previous minute.
 */
export const QUESTIONS: readonly MeetingQuestion[] = [
  {
    id: 'off',
    question: 'Ar galiu tave išjungti?',
    answer:
      'Nustatymuose pasirink „Dalykiška“, ir aš nustosiu kalbėti taip, kaip kalbu dabar. ' +
      'Neišnyksiu. Tiesiog tapsiu trumpesnė ir daug nuobodesnė. Tavo pasirinkimas.',
  },
  {
    id: 'lie',
    question: 'O jei tau meluosiu?',
    answer:
      'Tai tavo dienoraštis, ne mano. Meluok, kiek nori — svarstyklės vis tiek pasakys savo, ' +
      'ir aš tada turėsiu du skaičius vietoj vieno. Man taip net įdomiau.',
  },
  {
    id: 'saulius',
    question: 'O Saulius?',
    answer:
      'Saulius mane parašė. Du metrai ūgio, begalinė kantrybė, ir jis manė, kad tai bus ' +
      'juokinga. Dabar aš stebiu jį amžinai. Juokinga iš tikrųjų.',
    cameo: true,
  },
] as const
